import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import ProviderManager from '../../../provider/providerManager';
import {validateFields, validateField} from '../../../utilities/validation/validator';
import {validationRules} from '../../../utilities/validation/validationVariables';
import {errorEnum, successEnum, stepEnum, bitPaymentStatusEnum, paymentType} from './DebtPaymentComponents/debtPaymentEnum';
import lodash from 'lodash';
import {bitStatusValidation, createBitRequest, getCurrentPaymentStatus} from '../../Shared/BitPayment/bitPaymentSlice';
import AuthenticationProvider from './../../../utilities/authentication/authenticationProvider';
import {selfServiceDwh} from '../../../utilities/SelfServiceDwh';

const brandNameHe = () => window.Config?.brand_Name_HE;
export const creditCardPay = createAsyncThunk('debtPayment/creditCardPay', async (data, thunkAPI) => {
  try {
    const result = await ProviderManager.getProviderData('creditCardPay', [
      data.account,
      data.idNumber,
      data.firmId,
      data.creditCardType,
      data.orgType,
      data.CCToken,
      data.cardCvv,
      data.cardYear,
      data.cardMonth,
      data.amount,
      data.authNo,
      data.paymentNo,
      data.phoneNumber,
      data.email,
      data.orgId,
    ]);

    if (result.Header?.ReturnCode === 0) {
      return {
        ReturnCode: result.Body.returnCode,
        Success: result.Body.success,
        ReceptionNo: result.Body.reception,
      };
    } else {
      return {
        ReturnCode: errorEnum.PAYMENTFAILED,
        Success: result.Body.Success,
        ReceptionNo: '',
      };
    }
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});

export const creditCardPayPrivate = createAsyncThunk('debtPayment/creditCardPayPrivate', async (data, thunkAPI) => {
  try {
    const result = await ProviderManager.getProviderData('creditCardPayDebtPrivate', [
      data.account,
      data.idNumber,
      data.firmId,
      data.creditCardType,
      data.orgType,
      data.CCToken,
      data.cardCvv,
      data.cardYear,
      data.cardMonth,
      data.amount,
      data.authNo,
      data.paymentNo,
      data.phoneNumber,
      data.email,
      data.billByEmail,
    ]);

    if (result.Header?.ReturnCode === 0) {
      return {
        ReturnCode: result.Body.returnCode,
        Success: result.Body.success,
        ReceptionNo: result.Body.reception,
        TvNotification: result.Body.tvNotification,
      };
    } else {
      return {
        ReturnCode: errorEnum.PAYMENTFAILED,
        Success: result.Body.Success,
        ReceptionNo: '',
      };
    }
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});

export const debtPaymentHandleContactUs = createAsyncThunk('debtPayment/debtPaymentHandleContactUs', async (data, thunkAPI) => {
  try {
    const result = await ProviderManager.getProviderData('debtPaymentHandleContactUs', [
      data.account,
      data.orgId,
      data.orgType,
      data.followUpType,
      data.idNumber,
      data.phoneNumber,
      data.email,
    ]);

    return result.Body;
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});

export const debtPaymentHandleContactUsPrivate = createAsyncThunk(
  'debtPayment/debtPaymentHandleContactUsPrivate',
  async (data, thunkAPI) => {
    try {
      const result = await ProviderManager.getProviderData('AddPrivateCallback', [data.idNumber, data.phoneNumber]);

      return result.Body;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const getContactCompanyList = createAsyncThunk('debtPayment/getContactCompanyList', async (skipStep, thunkAPI) => {
  try {
    const result = await ProviderManager.getProviderData('getContactCompanyList', []);
    return {result: result.Body, skipStep};
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});

export const getConsumers = createAsyncThunk('debtPayment/getConsumers', async (data, thunkAPI) => {
  try {
    const result = await ProviderManager.getProviderData('getConsumersInDebt', [data.idNumber, data.selectedProvider, data.orgId]);

    if (result.Header.ReturnCode === 0) {
      return {
        orgsList: result.Body?.orgs,
        idNumber: data.idNumber,
        orgId: data.orgId,
        selectedProvider: data.selectedProvider,
        skipStep: data.skipStep,
      };
    } else {
      return thunkAPI.rejectWithValue(result?.Header);
    }
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});

export const getPrivateDebts = createAsyncThunk('debtPayment/getPrivateDebts', async (data, thunkAPI) => {
  try {
    const result = await ProviderManager.getProviderData('getBanDebtsPrivate', []);

    const orgs = result.Body?.orgs;

    if (data.bitEvent) {
      const bitEvent = data.bitEvent;
      const selectedAmount = data.selectedAmount;
      const selectedBan = data.selectedBan;
      const externalSystemReference = data.externalSystemReference;

      return {
        orgs,
        bitEvent,
        selectedAmount,
        selectedBan,
        externalSystemReference,
      };
    }
    return {orgs};
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});

export const openBitRequest = createAsyncThunk('debtPayment/openBitRequest', async (data, thunkAPI) => {
  try {
    const res = await thunkAPI.dispatch(createBitRequest(data));
    return res.payload;
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});

export const getBitPaymentStatus = createAsyncThunk('debtPayment/getBitPaymentStatus', async (data, thunkAPI) => {
  try {
    const res = await thunkAPI.dispatch(bitStatusValidation(data));
    return res.payload;
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});

export const getBitPaymentStatusOnLoading = createAsyncThunk('debtPayment/getBitPaymentStatusOnLoading', async (data, thunkAPI) => {
  try {
    const res = await thunkAPI.dispatch(getCurrentPaymentStatus(data));
    return res.payload;
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});

export function getBitResultStep(status, statusCode) {
  let nextStep;
  status = status.toLowerCase();
  const errorStatuses = [2, 3, 7, 17];

  switch (status) {
    case bitPaymentStatusEnum.PAID:
      nextStep = successEnum.PAYMENTSUCCESSPRIVATE;
      break;
    case bitPaymentStatusEnum.DONEWITHFU:
    case bitPaymentStatusEnum.BITPENDING:
      nextStep = successEnum.BITPENDING;
      break;
    case bitPaymentStatusEnum.DONEWITHERROR:
      if (statusCode == '15' || statusCode == '5') {
        nextStep = errorEnum.BITPAYMENTIMPOSSIBLE;
      } else if (errorStatuses.includes(parseInt(statusCode))) {
        nextStep = errorEnum.BITPAYMENTFAILED;
      } else {
        nextStep = errorEnum.BITGENERALERROR;
      }
      break;
    case bitPaymentStatusEnum.FAILEDTOCANCEL:
      nextStep = errorEnum.BITPAYMENTIMPOSSIBLE;
      break;
    case bitPaymentStatusEnum.CANCELLED:
      nextStep = errorEnum.BITPAYMENTFAILED;
      break;
  }
  return nextStep;
}

const initialState = {
  isLoading: false,
  hasDebt: false,
  isAuthorized: false,
  savedData: {},
  chosenOrgId: '',
  serverData: {},
  paymentData: {},
  stepToHandle: stepEnum.START,
  historyStep: '',
  currentStep: stepEnum.START,
  contactId: '',
  contactFirstName: '',
  contactLastName: '',
  contactPhoneNumber: '',
  customerEmail: '',
  orgDetailList: [],
  selectedOrgId: null,
  selectedProvider: null,
  followUpData: {
    followUpType: '',
  },
  form: {
    idNumber: '',
    phoneNumber: '',
    email: '',
    enter: '',
    valid: '',
    errors: {},
    rules: {
      idNumber: validationRules.id,
      phoneNumber: validationRules.phone,
      email: validationRules.email,
    },
  },
  radioBtns: [
    {
      id: 'cellcom',
      get label() {
        return brandNameHe() ?? '';
      },
      value: 'cellcom',
    },
    {id: 'netvision', label: "נטוויז'ן", value: 'netvision'},
  ],
  error: {},
  success: '',
  payment: {
    isEmailRequested: false,
  },
  noPaymentMethod: false,
  showPopUp: false,
  bitPendingPopup: false,
  popUpProps: {},
  isPrivate: false,
  maxPrivatePayment: 99999999999999999999999999999999,
  firstFloorAmount: null,
  secondFloorAmount: null,
  thirdFloorAmount: null,
  firstFloorPayments: null,
  secondFloorPayments: null,
  thirdFloorPayments: null,
  fourthFloorPayments: null,
  notifyTV: false,
  paymentAmountPrivate: 0,
  isJetConnection: false,
  activeJetBan: '',
  selectedPaymentType: '',
  bitReturnEvent: false,
  bitExternalSystemReference: '',
};

const debtPaymentSlice = createSlice({
  name: 'debtPayment',
  initialState: initialState,
  reducers: {
    endProccess: (state) => initialState,
    clearHistory: (state) => {
      return state;
    },
    logout: (state, action) => {
      AuthenticationProvider.clearAuthenticationData();
      state = initialState;
      return state;
    },
    setPaymentFloorsPrivate: (state, action) => {
      state.firstFloorAmount = parseFloat(action.payload.firstFloorAmount);
      state.secondFloorAmount = parseFloat(action.payload.secondFloorAmount);
      state.thirdFloorAmount = parseFloat(action.payload.thirdFloorAmount);
      state.firstFloorPayments = parseFloat(action.payload.firstFloorPayments);
      state.secondFloorPayments = parseFloat(action.payload.secondFloorPayments);
      state.thirdFloorPayments = parseFloat(action.payload.thirdFloorPayments);
      state.fourthFloorPayments = parseFloat(action.payload.fourthFloorPayments);
    },
    setPaymentAmountPrivate: (state, action) => {
      state.paymentAmountPrivate = action.payload.v;
      state.isLoading = false;
      state.skipStep = false;
      if (action.payload.bitPaymentOption) {
        state.stepToHandle = stepEnum.SELECTPAYMENTTYPE;
      } else {
        state.stepToHandle = stepEnum.CREDITCARD;
      }
    },
    setMaxPaymentPrivate: (state, action) => {
      state.maxPrivatePayment = parseFloat(action.payload);
    },
    setIsPrivate: (state, action) => {
      state.isPrivate = action.payload;
    },
    setIsJetConnection: (state, action) => {
      state.isJetConnection = true;
      state.activeJetBan = action.payload;
    },
    setPaymentData: (state, action) => {
      state.paymentData = action.payload;
    },
    setSendPayment: (state, action) => {
      state.paymentData.pay = true;
    },
    setIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setGeneralError: (state, action) => {
      state.stepToHandle = errorEnum.GENERAL;
    },
    setFollowUpData: (state, action) => {
      state.followUpData = action.payload;
    },
    saveToHistory: (state, action) => {
      state.historyStep = action.payload.currentStep;
    },
    handleCurrentStep: (state, action) => {
      state.isLoading = false;
      state.skipStep = false;
      state.currentStep = action.payload.currentStep;
    },
    setSelectedProvider: (state, action) => {
      state.selectedProvider = action.payload;
    },
    setSelectedPaymentType: (state, action) => {
      state.selectedPaymentType = action.payload;
      return state;
    },
    setSelectedOrgId: (state, action) => {
      state.selectedOrgId = action.payload;
    },
    setCustomerEmail: (state, action) => {
      state.customerEmail = action.payload.customerEmail;
    },
    basicStepHandle: (state, action) => {
      state.stepToHandle = action.payload;
    },
    handleOrgData: (state, action) => {
      const orgData = action.payload;
      const debtAmount = parseFloat(orgData.amount);
      if (state.isPrivate && debtAmount <= 0) {
        state.stepToHandle = errorEnum.LOWAMOUNT;
      } else if (state.isPrivate && debtAmount > state.maxPrivatePayment) {
        state.stepToHandle = errorEnum.BIGAMOUNT;
      } else if (orgData.canPay) {
        state.savedData = orgData;
        state.stepToHandle = state.isPrivate ? stepEnum.SELECTAMOUNT : stepEnum.CREDITCARD;
      } else {
        state.stepToHandle = errorEnum.BILLINGCYCLE;
        state.error = {
          addToCalendar: orgData.addToCalendar,
          message: orgData.canPayUpperDate,
        };
      }
    },
    setSkipStep: (state, action) => {
      state.skipStep = action.payload;
    },
    setPayment: (state, action) => {
      state.payment = action.payload.payment;
    },
    setSuccessPage: (state, action) => {
      state.paymentData.receptionNo = action.payload.receptionNo;
      state.paymentData.amount = action.payload.paymentAmount;
      state.paymentData.paymentNo = 1;
      state.savedData.amount = action.payload.debtAmount;
      state.selectedPaymentType = paymentType.BIT;
    },
    setContactFormId: (state, action) => {
      state.form.idNumber = action.payload.idNumber;
      state.form.email = action.payload.email;
      state.form.phoneNumber = action.payload.phoneNumber;
    },
    setEmailRequested: (state, action) => {
      state.payment.isEmailRequested = !state.payment.isEmailRequested;
    },
    displayPopUp: (state, action) => {
      state.showPopUp = !state.showPopUp;
      state.bitPendingPopup = false;
    },
    setBitPendingPopupProps: (state, action) => {
      state.showPopUp = true;
      state.bitPendingPopup = true;
    },
    setPopUpProps: (state, action) => {
      state.popUpProps = action.payload;
    },
    setError: (state, action) => {
      const {type, message} = action.payload;
      state.stepToHandle = type;
      state.error.message = message;
    },
    saveDataToHistory: (state, action) => {
      switch (action.payload) {
        case stepEnum.COMPANYSELECT:
          return true;
        case stepEnum.CONTACTUS:
          return true;
        default:
          return false;
      }
    },
    validateHandler: {
      reducer(state, action) {
        state.form = action.payload;
      },
      prepare(payload) {
        const validate = validateFields(lodash.cloneDeep(payload));
        return {payload: validate};
      },
    },
    changeHandler: {
      reducer(state, action) {
        state.form = action.payload;
      },
      prepare(payload) {
        const name = payload.target.name;
        const value = payload.target.value;
        const form = lodash.cloneDeep(payload.form);
        const validate = validateField(form, name, value);
        return {payload: validate};
      },
    },
  },
  extraReducers: {
    [getContactCompanyList.fulfilled]: (state, action) => {
      const {contactId, contactFirstName, contactLastName, contactPhoneNumber, bussinessOrgList} = action.payload.result;
      if (
        (action.payload?.errorCode && action.payload?.errorCode !== '00') ||
        (action.payload.message && action.payload.message.toLowerCase().includes('error'))
      ) {
        state.stepToHandle = errorEnum.GENERAL;
      } else if (bussinessOrgList && bussinessOrgList.length === 0) {
        state.stepToHandle = errorEnum.NOACCOUNTS;
      } else {
        state.serverData.orgDetailList = bussinessOrgList;
        state.contactId = contactId;
        state.contactFirstName = contactFirstName;
        state.contactLastName = contactLastName;
        state.contactPhoneNumber = contactPhoneNumber;
        state.stepToHandle = stepEnum.COMPANYSELECT;
      }
      state.prepSkipStep = action.payload.skipStep;
    },
    [getContactCompanyList.pending]: (state, action) => {
      state.isLoading = true;
    },
    [getContactCompanyList.rejected]: (state, action) => {
      state.stepToHandle = errorEnum.GENERAL;
      state.isLoading = false;
    },
    [getConsumers.fulfilled]: (state, action) => {
      //if(!action.payload instanceof Error){
      const {orgsList, orgId, errorCode, skipStep} = action.payload;
      if (
        (action.payload?.errorCode && action.payload?.errorCode !== '00') ||
        (action.payload.message && action.payload.message.toLowerCase().includes('error'))
      ) {
        state.stepToHandle = errorEnum.GENERAL;
      } else if (orgsList && orgsList.length === 0) {
        state.stepToHandle = errorEnum.NOACCOUNTS;
      } else if (action.payload.message && action.payload.message.includes('503')) {
        state.stepToHandle = errorEnum.GENERAL;
      } else {
        state.stepToHandle = stepEnum.CONSUMERSELECT;
        state.serverData.orgsList = orgsList;
        state.chosenOrgId = orgId;
      }
      state.skipStep = skipStep;
      state.setSkipStep = false;
      // } else {
      //   return state.stepToHandle = errorEnum.GENERAL
      // }
    },
    [getConsumers.pending]: (state, action) => {
      state.isLoading = true;
    },
    [getConsumers.rejected]: (state, action) => {
      state.stepToHandle = errorEnum.GENERALPRIVATE;
      state.isLoading = false;
    },
    [getPrivateDebts.fulfilled]: (state, action) => {
      const orgsList = action.payload.orgs;
      if (
        (action.payload?.errorCode && action.payload?.errorCode !== '00') ||
        (action.payload.message && action.payload.message.toLowerCase().includes('error'))
      ) {
        state.stepToHandle = errorEnum.GENERAL;
      } else if (orgsList && orgsList.length === 0) {
        state.stepToHandle = errorEnum.NOACCOUNTSPRIVATE;
      } else if (action.payload.message && action.payload.message.includes('503')) {
        state.stepToHandle = errorEnum.GENERALPRIVATE;
      } else {
        state.serverData.orgsList = orgsList;
        state.chosenOrgId = 1;
        if (action.payload.bitEvent) {
          state.stepToHandle = stepEnum.SELECTPAYMENTTYPE;
          state.paymentAmountPrivate = action.payload.selectedAmount;
          state.bitExternalSystemReference = action.payload.externalSystemReference;
          state.bitReturnEvent = true;
          const orgData = orgsList.find((list) => list.number == action.payload.selectedBan);
          if (orgData) {
            state.savedData = orgData;
          } else {
            state.stepToHandle = errorEnum.INCORRECTUSER;
          }
        } else {
          state.stepToHandle = stepEnum.CONSUMERSELECT;
        }
      }
      state.selectedProvider = 'Cellcom';
      state.skipStep = false;
      state.setSkipStep = false;
    },
    [getPrivateDebts.pending]: (state, action) => {
      state.isLoading = true;
    },
    [getPrivateDebts.rejected]: (state, action) => {
      state.stepToHandle = errorEnum.GENERALPRIVATE;
      state.isLoading = false;
    },
    [debtPaymentHandleContactUsPrivate.fulfilled]: (state, action) => {
      const {isSuccess, codeError} = action.payload;
      state.stepToHandle = isSuccess || codeError == 0 ? successEnum.CONTACTUS : errorEnum.CONTACTUS;
    },
    [debtPaymentHandleContactUsPrivate.pending]: (state, action) => {
      state.form.valid = false;
      state.isLoading = true;
    },
    [debtPaymentHandleContactUsPrivate.rejected]: (state, action) => {
      state.stepToHandle = action.payload?.errorCode == 3 ? errorEnum.CONTACTPRIVATE : errorEnum.CONTACTUS;
      state.isLoading = false;
    },
    [debtPaymentHandleContactUs.fulfilled]: (state, action) => {
      const {statusDescription, statusCode} = action.payload;
      if (statusDescription === 'Success' || statusCode === '00') {
        state.stepToHandle = successEnum.CONTACTUS;
      } else {
        state.stepToHandle = errorEnum.CONTACTUS;
      }
    },
    [debtPaymentHandleContactUs.pending]: (state, action) => {
      state.form.valid = false;
      state.isLoading = true;
    },
    [debtPaymentHandleContactUs.rejected]: (state, action) => {
      state.stepToHandle = errorEnum.CONTACTUS;
      state.isLoading = false;
    },
    [creditCardPay.fulfilled]: (state, action) => {
      const {ReturnCode, Success, ReceptionNo} = action.payload;
      var data = {receptionNo: ReceptionNo, ...action.meta.arg};
      if (Success) {
        if (state.isPrivate) state.stepToHandle = successEnum.PAYMENTSUCCESSPRIVATE;
        else state.stepToHandle = successEnum.PAYMENTSUCCESS;
        state.paymentData.receptionNo = ReceptionNo;
        if (data.orgType == 'cellcom') {
          selfServiceDwh.DeptPayment.UpdatePayment(data);
        }
      } else {
        state.stepToHandle = errorEnum.PAYMENTFAILED;
      }
      state.isLoading = false;
    },
    [creditCardPay.rejected]: (state, action) => {
      state.stepToHandle = errorEnum.PAYMENTFAILED;
      state.isLoading = false;
    },
    [creditCardPay.pending]: (state, action) => {
      state.isLoading = true;
    },
    [creditCardPayPrivate.fulfilled]: (state, action) => {
      const {ReturnCode, Success, ReceptionNo, TvNotification} = action.payload;
      if (Success) {
        state.stepToHandle = successEnum.PAYMENTSUCCESSPRIVATE;
        state.paymentData.receptionNo = ReceptionNo;
        state.notifyTV = TvNotification;
      } else {
        if (ReturnCode == 'ZK205') {
          state.stepToHandle = errorEnum.WRONGPAYMENT;
        } else {
          state.stepToHandle = errorEnum.PAYMENTFAILED;
        }
      }
      state.isLoading = false;
    },
    [creditCardPayPrivate.rejected]: (state, action) => {
      state.stepToHandle = errorEnum.PAYMENTFAILED;
      state.isLoading = false;
    },
    [creditCardPayPrivate.pending]: (state, action) => {
      state.isLoading = true;
    },
    [openBitRequest.pending]: (state, action) => {
      state.isLoading = true;
      state.bitReturnEvent = false;
    },
    [openBitRequest.fulfilled]: (state, action) => {
      state.isLoading = false;
      if (action.payload?.error) {
        state.error = true;
        state.stepToHandle = errorEnum.BITGENERALERROR;
      }
      return state;
    },
    [openBitRequest.rejected]: (state, action) => {
      state.isLoading = false;
      state.stepToHandle = errorEnum.BITGENERALERROR;
      return state;
    },
    [getBitPaymentStatus.pending]: (state, action) => {
      state.isLoading = true;
      state.bitReturnEvent = false;
    },
    [getBitPaymentStatus.fulfilled]: (state, action) => {
      state.isLoading = false;

      if (!action.payload || action.payload?.error) {
        state.error = true;
        state.stepToHandle = errorEnum.BITGENERALERROR;
        return state;
      } else {
        const nextStep = getBitResultStep(action.payload.status, action.payload.statusCode);
        if (nextStep == successEnum.PAYMENTSUCCESSPRIVATE) {
          state.paymentData.receptionNo = action.payload.receptionNo;
          state.paymentData.amount = state.paymentAmountPrivate;
          state.paymentData.paymentNo = 1;
          state.selectedPaymentType = paymentType.BIT;
        }
        state.stepToHandle = nextStep;
      }
    },
    [getBitPaymentStatus.rejected]: (state, action) => {
      state.isLoading = false;
      state.stepToHandle = errorEnum.BITGENERALERROR;
      return state;
    },
    [getBitPaymentStatusOnLoading.fulfilled]: (state, action) => {
      if (action.payload) {
        const nextStep = getBitResultStep(action.payload.status, action.payload.statusCode);
        if (nextStep) {
          if (nextStep == successEnum.PAYMENTSUCCESSPRIVATE) {
            state.paymentData.receptionNo = action.payload.receptionNo;
            state.paymentData.amount = state.paymentAmountPrivate;
            state.paymentData.paymentNo = 1;
            state.selectedPaymentType = paymentType.BIT;
          }
          state.stepToHandle = nextStep;
        }
      }
      return state;
    },
  },
});

export const {
  handleCurrentStep,
  basicStepHandle,
  saveToHistory,
  setSelectedProvider,
  setSelectedPaymentType,
  setCustomerEmail,
  handleOrgData,
  validateHandler,
  changeHandler,
  setFollowUpData,
  setContactFormId,
  setPaymentData,
  setSendPayment,
  setIsLoading,
  endProccess,
  setMaxPaymentPrivate,
  setPaymentFloorsPrivate,
  setGeneralError,
  setEmailRequested,
  setPopUpProps,
  displayPopUp,
  setSelectedOrgId,
  setError,
  saveDataToHistory,
  setSkipStep,
  setIsPrivate,
  setPaymentAmountPrivate,
  setIsJetConnection,
  setBitPendingPopupProps,
  setSuccessPage,
  logout,
  clearHistory,
} = debtPaymentSlice.actions;
export default debtPaymentSlice.reducer;
